import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Col,
  CustomInput,
  FormGroup,
  FormText,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import { useAuth } from '../../../commons/Auth';
import { BooleanField, DataTable, Field } from '../../../commons/DataTable';
import ContractsCheckbox from './ContractsCheckbox';
import { useAlert } from 'react-alert';
import DisponibilityField from './DisponibilityField';
import TypeField from './TypeField';
import AgreementUserField from '../../../commons/DataTable/AgreementUserField';

const INITAL_CONTRACT_FORM_VALUES = {
  name: '',
  activity: '',
  type: '',
  documentType: '',
  supplier: '',
  attachedFile: '',
  expireDate: '',
  noExpireDate: false,
  typeName: '',
};

const INITAL_CONTRACT_ERRORS = {
  name: false,
  activity: false,
  type: false,
  documentType: false,
  expireDate: false,
};

function AddContractStepper({
  profile,
  activities,
  documentTypes,
  agreementTypes,
  suppliers,
  documents,
  onFilter,
  total,
  limit,
  offset,
  onSelectChange,
  onSelectAll,
  selectedAll,
  selectedDocuments,
  fetchSuppliers,
  fetchAgreementTypes,
  uploadAgreements,
  setSelectedSupplier,
  setRequiredDocuments,
  missDocumentsRequired,
  setSelectedActivityId,
  documentsForRequired,
}) {
  const [t] = useTranslation('AGREEMENTS_ADMIN_SUPPLIER');
  const alert = useAlert();
  const [{ newIsAdmin } = {}] = useAuth();

  const [addNewContractModal, setAddNewContractModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formValues, setFormValues] = useState(INITAL_CONTRACT_FORM_VALUES);
  const [errors, setErrors] = useState(INITAL_CONTRACT_ERRORS);
  const [showPriorityWarning, setShowPriorityWarning] = useState(false);

  const missingPriorityDocs = useMemo(() => {
    if (
      !Array.isArray(documentsForRequired) ||
      !Array.isArray(selectedDocuments)
    )
      return false;
    const priorityIds = documentsForRequired
      .filter((doc) => doc.priority === true)
      .map((doc) => doc._id);
    const selectedDocumentsIds = selectedDocuments.map((doc) => doc._id);
    const result = priorityIds.some((id) => !selectedDocumentsIds.includes(id));

    if (result) {
      setShowPriorityWarning(false);
    }
    return result;
  }, [documentsForRequired, selectedDocuments]);

  const toggleAddAgreementModal = () => {
    setCurrentStep(1);
    setFormValues(INITAL_CONTRACT_FORM_VALUES);
    setErrors(INITAL_CONTRACT_ERRORS);
    setAddNewContractModal(!addNewContractModal);
  };

  function handleContractChange(redirect = false) {
    const name = formValues.name;
    const activity = formValues.activity;
    const type = formValues.type;
    const typeName = formValues.typeName;
    const supplier = formValues.supplier;
    const attachedFile = formValues.attachedFile;
    const agreementType = 'SUPPLIER_ADMIN';
    const documentType = formValues.documentType;

    let newErrors = {};
    if (formValues.attachedFile !== '' && formValues.documentType === '') {
      newErrors.documentType = true;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors({ ...errors, ...newErrors });
      return;
    }

    const fileSize = (attachedFile.size / 1024 ** 2).toFixed(2);

    if (fileSize > 20) {
      alert.info('File troppo grande. Max 20Mb');
    } else {
      const data = new FormData();
      data.append('name', name);
      data.append('activity', activity);
      data.append('type', type);
      data.append('typeName', typeName);
      data.append('supplier', supplier);
      data.append('file', attachedFile);
      data.append('agreementType', agreementType);
      data.append('selectedDocuments', JSON.stringify(selectedDocuments));
      data.append('documentType', documentType);

      if (typeName === 'Tipo Contratto Amminstratore') {
        data.append('expireDate', formValues.expireDate);
      }

      uploadAgreements(data, toggleAddAgreementModal, redirect);
    }
  }

  const goToNextStep = () => {
    let newErrors = {};
    if (currentStep === 1) {
      if (formValues.name === '') {
        newErrors.name = true;
      }
      if (formValues.activity === '') {
        newErrors.activity = true;
      }
      if (formValues.type === '') {
        newErrors.type = true;
      }
      if (formValues.supplier === '') {
        newErrors.supplier = true;
      }
    }

    // if (currentStep === 2) {
    //   if (
    //     formValues.typeName === 'Tipo Contratto Amminstratore' &&
    //     formValues.expireDate === '' &&
    //     formValues.noExpireDate === false
    //   ) {
    //     newErrors.expireDate = true;
    //   }
    // }

    if (Object.keys(newErrors).length > 0) {
      setErrors({ ...errors, ...newErrors });
      return;
    }

    if (currentStep === 2 && missingPriorityDocs) {
      setShowPriorityWarning(true);
      return;
    }

    if (currentStep < 3) setCurrentStep(currentStep + 1);
    setShowPriorityWarning(false);
  };

  const goToPreviousStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const onRowClick = (row) => {
    const { _id } = row;
    onSelectChange(_id);
  };

  useEffect(() => {
    if (formValues.activity && formValues.activity !== '') {
      fetchSuppliers(formValues.activity);
      fetchAgreementTypes(formValues.activity);
    }
  }, [formValues.activity]);

  return (
    <React.Fragment>
      <Button
        color="primary"
        style={{
          margin: 0,
          fontSize: '1rem',
          width: '100%',
        }}
        onClick={(event) => {
          event.stopPropagation();
          toggleAddAgreementModal();
        }}
      >
        {t('BTN_TITLE')}
      </Button>

      <Modal
        isOpen={addNewContractModal}
        toggle={toggleAddAgreementModal}
        className="new-file-modal agreement-label"
        style={{ maxWidth: '70%' }}
      >
        <ModalHeader
          toggle={toggleAddAgreementModal}
          style={{ paddingBottom: '0px' }}
        >
          {t('TITLE')}
        </ModalHeader>
        <ModalBody>
          {currentStep === 1 && (
            <>
              <div
                style={{
                  fontSize: '15px',
                  fontFamily: '"Open Sans", sans-serif',
                  textAlign: 'center',
                  marginBottom: '10px',
                }}
              >
                Passo 1 di 3
              </div>
              <Row>
                <Col sm="12">
                  <FormGroup>
                    <Label>{t('NAME')}</Label>
                    <Input
                      type="text"
                      name="name"
                      maxLength="50"
                      defaultValue={formValues.name}
                      placeholder={t('SELECT_NAME')}
                      onChange={(e) => {
                        setFormValues({
                          ...formValues,
                          name: e.target.value,
                        });
                        setErrors({
                          ...errors,
                          name: false,
                        });
                      }}
                    />
                    {errors.name === true && (
                      <FormText className="error-message">
                        {t('NAME_ERROR')}
                      </FormText>
                    )}
                  </FormGroup>
                </Col>

                <Col sm="12">
                  <FormGroup>
                    <Label>{t('ACTIVITY')}</Label>
                    <CustomInput
                      id="activity"
                      type="select"
                      name="activity"
                      value={formValues.activity}
                      placeholder={t('SELECT')}
                      onChange={(e) => {
                        setFormValues({
                          ...formValues,
                          activity: e.target.value,
                          type: '',
                          typeName: '',
                          supplier: '',
                        });
                        setSelectedActivityId(e.target.value);
                        setErrors({
                          ...errors,
                          activity: false,
                        });
                      }}
                      style={{
                        color: formValues.activity === '' ? '#aaa' : '#000',
                      }}
                    >
                      <option value="" className="placeholder-option">
                        Seleziona un'attività
                      </option>
                      {activities.map((activity, i) => (
                        <option key={i} value={activity._id}>
                          {activity.name}
                        </option>
                      ))}
                    </CustomInput>
                    {errors.activity === true && (
                      <FormText className="error-message">
                        {t('ACTIVITY_ERROR')}
                      </FormText>
                    )}
                  </FormGroup>
                </Col>

                <Col sm="12">
                  <FormGroup>
                    <Label>{t('TYPE')}</Label>
                    <CustomInput
                      id="type"
                      type="select"
                      name="type"
                      value={formValues.type}
                      placeholder={t('SELECT')}
                      disabled={
                        !formValues.activity && formValues.activity === ''
                      }
                      onChange={(e) => {
                        const selectedTypeId = e.target.value;

                        const selectedType = agreementTypes.find(
                          (type) => type._id === selectedTypeId
                        );

                        setRequiredDocuments(
                          selectedType
                            ? selectedType.requiredDocuments
                              ? selectedType.requiredDocuments
                              : []
                            : []
                        );
                        setFormValues({
                          ...formValues,
                          type: selectedTypeId,
                          typeName: selectedType ? selectedType.name : '',
                          supplier: '',
                        });
                        setErrors({
                          ...errors,
                          type: false,
                        });
                      }}
                      style={{
                        color: formValues.type === '' ? '#aaa' : '#000',
                      }}
                    >
                      <option value="" className="placeholder-option">
                        Seleziona la tipologia di contratto
                      </option>
                      {agreementTypes.map((type, i) => (
                        <option key={i} value={type._id}>
                          {type.name}
                        </option>
                      ))}
                    </CustomInput>
                    {errors.type === true && (
                      <FormText className="error-message">
                        {t('TYPE_ERROR')}
                      </FormText>
                    )}
                  </FormGroup>
                </Col>

                <Col sm="12">
                  <FormGroup>
                    <Label>{t('SUPPLIER')}</Label>
                    <CustomInput
                      id="supplier"
                      type="select"
                      name="supplier"
                      value={formValues.supplier}
                      placeholder={t('SELECT')}
                      disabled={!formValues.type && formValues.type === ''}
                      onChange={(e) => {
                        setFormValues({
                          ...formValues,
                          supplier: e.target.value,
                        });
                        setErrors({
                          ...errors,
                          supplier: false,
                        });
                        setSelectedSupplier(e.target.value);
                      }}
                      style={{
                        color: formValues.supplier === '' ? '#aaa' : '#000',
                      }}
                    >
                      <option value="" className="placeholder-option">
                        Seleziona un fornitore
                      </option>
                      {suppliers.map((supplier, i) => (
                        <option key={i} value={supplier._id}>
                          {supplier.businessName}
                        </option>
                      ))}
                    </CustomInput>
                    {errors.supplier === true && (
                      <FormText className="error-message">
                        {t('SUPPLIER_ERROR')}
                      </FormText>
                    )}
                  </FormGroup>
                </Col>
              </Row>
            </>
          )}

          {/* {currentStep === 2 && (
            <>
              <div
                style={{
                  fontSize: '15px',
                  fontFamily: '"Open Sans", sans-serif',
                  textAlign: 'center',
                  marginBottom: '10px',
                }}
              >
                Passo 2 di 4
              </div>
              {formValues.typeName === '' && (
                <Row>
                  <Col xs="12">
                    Non ci sono ulteriori elementi da poter inserire.
                  </Col>
                </Row>
              )}
              {formValues.typeName === 'Tipo Contratto Amminstratore' && (
                <Row>
                  <Col xs="12">
                    <FormGroup>
                      <Label>{t('EXPIRE_DATE')}</Label>
                      <DateInput
                        name="expireDate"
                        value={formValues.expireDate}
                        onChange={(val) => {
                          setFormValues({
                            ...formValues,
                            expireDate: val,
                          });
                          setErrors({
                            ...errors,
                            expireDate: false,
                          });
                        }}
                        disabled={formValues.noExpireDate}
                      />
                      {errors.expireDate === true && (
                        <FormText className="error-message">
                          {t('DATES_ERROR')}
                        </FormText>
                      )}
                    </FormGroup>
                  </Col>

                  <Col xs="12">
                    <FormGroup>
                      <ContFilterCheckbox>
                        <CustomInput
                          type="checkbox"
                          id="noExpireDate"
                          name="noExpireDate"
                          label={t('NO_EXPIRE_DATE')}
                          checked={formValues.noExpireDate}
                          onChange={(e) => {
                            e.stopPropagation();
                            setFormValues({
                              ...formValues,
                              noExpireDate: !formValues.noExpireDate,
                              expireDate: '',
                            });
                            setErrors({
                              ...errors,
                              expireDate: false,
                            });
                          }}
                        />
                      </ContFilterCheckbox>
                    </FormGroup>
                  </Col>
                </Row>
              )}
            </>
          )} */}

          {currentStep === 2 && (
            <>
              <div
                style={{
                  fontSize: '15px',
                  fontFamily: '"Open Sans", sans-serif',
                  textAlign: 'center',
                  marginBottom: '10px',
                }}
              >
                Passo 2 di 3
              </div>
              <div
                style={{
                  fontSize: '15px',
                  fontFamily: '"Open Sans", sans-serif',
                  textAlign: 'center',
                  marginBottom: '10px',
                }}
              >
                Scegli gli allegati dell'amministratore e del fornitore da
                inserire nel tuo contratto
              </div>

              {/* {missDocumentsRequired && (
                <div
                  style={{
                    fontSize: '15px',
                    fontFamily: '"Open Sans", sans-serif',
                    textAlign: 'center',
                    marginBottom: '5px',
                  }}
                >
                  <p
                    style={{
                      color: 'red',
                      textDecoration: 'underline',
                      fontWeight: 'bold',
                    }}
                  >
                    ATTENZIONE: abbiamo individuato che alcuni documenti
                    richiesti per questo tipo di contratto non sono ancora stati
                    caricati! Preghiamo di controllare l'effettiva mancanza dei
                    documenti prima di continuare.
                  </p>
                  Sicuro di voler continuare?
                </div>
              )} */}

              <div
                style={{
                  fontWeight: 'bold',
                  fontSize: '15px',
                  fontFamily: '"Open Sans", sans-serif',
                  textAlign: 'center',
                  marginBottom: '10px',
                }}
              >
                Vuoi un contrato personalizzato?{' '}
                <a href="#" onClick={(e) => e.preventDefault()}>
                  Richiedi una consulenza
                </a>
              </div>
              <DataTable
                data={documents}
                total={total}
                offset={offset}
                limit={limit}
                onFilter={onFilter}
                docStepper={true}
                emptyText={t('COMMON:NO_DATA')}
              >
                <Field
                  title="Documento"
                  source="file.name"
                  className="text-left"
                />
                <TypeField
                  title="Tag"
                  source="type.name"
                  className="text-left"
                />
                <BooleanField title="Richiesto" source="priority" />
                <DisponibilityField title="Disponibilità" />
                <AgreementUserField
                  title="Intestatario"
                  source="type.name"
                  className="text-left"
                />
                <ContractsCheckbox
                  selected={selectedDocuments}
                  onSelectChange={onRowClick}
                  onClick={(e) => e.stopPropagation()}
                  title={
                    <input
                      type="checkbox"
                      checked={selectedAll}
                      onChange={({ target: { checked } }) =>
                        onSelectAll(checked)
                      }
                    />
                  }
                />
              </DataTable>
            </>
          )}

          {currentStep === 3 && (
            <>
              <div
                style={{
                  fontSize: '15px',
                  fontFamily: '"Open Sans", sans-serif',
                  textAlign: 'center',
                  marginBottom: '10px',
                }}
              >
                Passo 3 di 3
              </div>
              <div
                style={{
                  fontSize: '15px',
                  fontFamily: '"Open Sans", sans-serif',
                  textAlign: 'center',
                  marginBottom: '5px',
                }}
              >
                <p
                  style={{
                    color: 'red',
                    textDecoration: 'underline',
                    fontWeight: 'bold',
                  }}
                >
                  ATTENZIONE: caricando un documento in questo modo si va ad
                  aggiornare la tipologia del documento selezionato (tranne per
                  i documenti Liberi)!
                </p>
                Sicuro di voler continuare?
              </div>
              <Row>
                <Col sm="12">
                  <FormGroup>
                    <Label for="selectContract">
                      {t('FILES:CHOOSE_FILE')}
                      &nbsp;({t('FILES:MAX_UPLOAD_FILE_SIZE')})
                    </Label>
                    <CustomInput
                      type="file"
                      id="attachedFile"
                      name="attachedFile"
                      onChange={(event) =>
                        setFormValues({
                          ...formValues,
                          attachedFile: event.target.files[0],
                        })
                      }
                    />
                  </FormGroup>
                </Col>
              </Row>
              {formValues.attachedFile !== '' && (
                <div
                  style={{
                    margin: '5px',
                    backgroundColor: '#F7F8FA',
                  }}
                >
                  <Row>
                    <Col sm="12">
                      <span style={{ overflowX: 'hidden' }}>
                        {formValues.attachedFile?.name}
                      </span>
                    </Col>

                    <Col sm="12">
                      <FormGroup>
                        <Label>{t('DOCUMENT_TYPE')}</Label>
                        <CustomInput
                          id="documentType"
                          type="select"
                          name="documentType"
                          value={formValues.documentType}
                          placeholder={t('SELECT')}
                          onChange={(e) => {
                            setFormValues({
                              ...formValues,
                              documentType: e.target.value,
                            });
                            setErrors({
                              ...errors,
                              documentType: false,
                            });
                          }}
                        >
                          <option value="">-</option>
                          {documentTypes.map((type, i) => (
                            <option key={i} value={type._id}>
                              {type.name}
                            </option>
                          ))}
                        </CustomInput>
                        {errors.type === true && (
                          <FormText className="error-message">
                            {t('DOCUMENT_TYPE_ERROR')}
                          </FormText>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>
                </div>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={goToPreviousStep}
            disabled={currentStep === 1}
          >
            Indietro
          </Button>
          {currentStep < 3 && (
            <Button
              color="primary"
              onClick={goToNextStep}
              disabled={currentStep === 3}
              style={{ margin: 0, marginLeft: '5px' }}
            >
              Avanti
            </Button>
          )}
          {currentStep === 3 && (
            <Button
              color="success"
              onClick={() => {
                handleContractChange();
              }}
              style={{ margin: 0, marginLeft: '5px' }}
            >
              Salva ed esci
            </Button>
          )}
          {currentStep === 3 && (
            <Button
              color="success"
              onClick={() => handleContractChange(true)}
              style={{ margin: 0, marginLeft: '5px' }}
            >
              Salva e vai al contratto
            </Button>
          )}
          {currentStep === 2 && showPriorityWarning && (
            <div style={{ width: '100%', marginTop: '10px' }}>
              <div
                style={{
                  fontSize: '15px',
                  fontFamily: '"Open Sans", sans-serif',
                  textAlign: 'center',
                  marginBottom: '5px',
                }}
              >
                <p
                  style={{
                    color: 'red',
                    textDecoration: 'underline',
                    fontWeight: 'bold',
                    marginBottom: '5px',
                  }}
                >
                  ATTENZIONE: alcuni documenti richiesti non sono stati
                  selezionati!
                </p>
                <p
                  style={{
                    color: 'red',
                    textDecoration: 'underline',
                    fontWeight: 'bold',
                    marginBottom: '10px',
                    textAlign: 'center',
                  }}
                >
                  Sei sicuro di voler continuare?
                </p>
              </div>
              <div style={{ textAlign: 'center' }}>
                <Button
                  color="warning"
                  size="sm"
                  onClick={() => {
                    setShowPriorityWarning(false);
                    setCurrentStep(3);
                  }}
                >
                  Continua lo stesso
                </Button>
              </div>
            </div>
          )}
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
}

export default AddContractStepper;
